import { useState } from 'react';
import { NumericFormat } from 'react-number-format';
import {
  formatDateValue,
  formatDateValueForInput,
  dateInputToUnix,
  dateToUnix,
  addDaysToDateValue,
  addBusinessDay,
  getDateFromValue,
  getDaysBetween,
  formatDateYYYYMMDD,
} from '../common/UtilityFunctions';
import useGetLossDraftRepair from '../common/hooks/useGetLossDraftRepair';
import {
  LossDraftRepair,
  LossDraftDateValue,
  LossDraftContractor,
  LossDraftExtension,
  LossDraftInspection,
  QidFieldName,
} from '../types/LossDraftRepairTypes';
import useQidList from '../common/hooks/useQidList';
import { useAuthContext } from '../contexts/AuthContext';
import styles from '../css/LossDraftRepair.module.css';

interface LossDraftRepairDetailsProps {
  loanNumber: string;
  claimId: string;
  onBack: () => void;
}

const getRepairsDueIn = (repairDueDate: LossDraftDateValue): string => {
  const dueDate = getDateFromValue(repairDueDate);

  if (!dueDate) {
    return '';
  }

  const today = new Date();
  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const normalizedDueDate = new Date(dueDate.getFullYear(), dueDate.getMonth(), dueDate.getDate());
  const days = Math.ceil((normalizedDueDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24));

  if (days === 0) {
    return 'Due today';
  }

  if (days < 0) {
    return `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`;
  }

  return `${days} day${days === 1 ? '' : 's'}`;
};

const isLoanInDefault = (repairDueDate: LossDraftDateValue): string => {
  const dueDate = getDateFromValue(repairDueDate);

  if (!dueDate) {
    return '';
  }

  return dueDate.getTime() < new Date().getTime() ? 'Yes' : 'No';
};

interface FieldRowProps {
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}

const FieldRow = ({ label, value, valueClassName }: FieldRowProps) => (
  <div className={styles.fieldRow}>
    <div className={styles.fieldLabel}>{label}</div>
    <div className={`${styles.fieldValue} ${valueClassName ?? ''}`}>{value || '\u00A0'}</div>
  </div>
);

const LossDraftRepairDetails = ({ loanNumber, claimId, onBack }: LossDraftRepairDetailsProps) => {
  const {
    lossDraftRepair,
    loadingLossDraftRepair,
    hasLoadedLossDraftRepair,
    lossDraftRepairError,
    updateLossDraftRepair,
  } = useGetLossDraftRepair(loanNumber, claimId);
  const { qid: claimStatusQid } = useQidList('LDR1_CD.014');
  const { qid: typeOfLossQid } = useQidList('LDR1_CD.015');
  const { qid: delinquencyStatusQid } = useQidList('LDR1_CD.017');
  const { qid: lossDraftCheckStatusQid } = useQidList('LDR1_CD.018');
  const { qid: milestoneStatusQid } = useQidList('LDR1_CD.019');
  const { qid: repairFileClosedStatusQid } = useQidList('LDR1_CD.028');
  const { qid: exceptionCodeQid } = useQidList('LDR1_CD.029');
  const { qid: inspectionTypeQid } = useQidList('LDR1_CD.C01');

  const [pendingChanges, setPendingChanges] = useState<Partial<LossDraftRepair>>({});
  const [contractorErrors, setContractorErrors] = useState<Record<number, string>>({});
  const [extensionErrors, setExtensionErrors] = useState<Record<number, string>>({});
  const [inspectionErrors, setInspectionErrors] = useState<Record<number, string>>({});

  const [savingExtensionIndex, setSavingExtensionIndex] = useState<number | null>(null);
  const [savingInspectionIndex, setSavingInspectionIndex] = useState<number | null>(null);

  const [delayExplanationModal, setDelayExplanationModal] = useState<{
    index: number;
    value: string;
  } | null>(null);

  const [saving, setSaving] = useState(false);
  const { userId } = useAuthContext();

  if (loadingLossDraftRepair || !hasLoadedLossDraftRepair) {
    return (
      <div className={styles.page}>
        <div className={styles.message}>Loading Loss Draft Repair details...</div>
      </div>
    );
  }

  if (lossDraftRepairError) {
    return (
      <div className={styles.page}>
        <button type='button' className={styles.backButton} onClick={onBack}>
          Back to Claims
        </button>

        <div className={styles.errorMessage}>{lossDraftRepairError}</div>
      </div>
    );
  }

  if (!lossDraftRepair) {
    return (
      <div className={styles.page}>
        <button type='button' className={styles.backButton} onClick={onBack}>
          Back to Claims
        </button>

        <div className={styles.message}>Loss Draft Repair record not found.</div>
      </div>
    );
  }

  const agingOfLoan = getDaysBetween(lossDraftRepair.dateInitialDetailsAdded, new Date());
  const repairDueDate = pendingChanges.repairDueDate ?? lossDraftRepair?.repairDueDate;
  const extensionFollowUpDate = pendingChanges.extensionFollowUpDate ?? lossDraftRepair.extensionFollowUpDate;
  const defaultLetterDueDate = pendingChanges.defaultLetterDueDate ?? lossDraftRepair.defaultLetterDueDate;
  const repairFileClosed = pendingChanges.repairFileClosed ?? lossDraftRepair.repairFileClosed ?? false;

  const hasPendingChanges = Object.keys(pendingChanges).length > 0;
  const savedContractors = lossDraftRepair.contractor_array ?? [];
  const savedExtensions = lossDraftRepair.extension_array ?? [];
  const savedInspections = lossDraftRepair.inspection_array ?? [];

  const contractors = pendingChanges.contractor_array ?? savedContractors;
  const extensions = pendingChanges.extension_array ?? savedExtensions;
  const inspections = pendingChanges.inspection_array ?? savedInspections;

  const isAddingContractor = contractors.length > savedContractors.length;
  const isAddingExtension = extensions.length > savedExtensions.length;
  const isAddingInspection = inspections.length > savedInspections.length;

  const handleSave = async () => {
    if (!hasPendingChanges) {
      return;
    }

    const contractorValidationErrors: Record<number, string> = {};
    const extensionValidationErrors: Record<number, string> = {};
    const inspectionValidationErrors: Record<number, string> = {};

    contractors.forEach((contractor) => {
      if (!contractor.contractorName?.trim()) {
        contractorValidationErrors[contractor.$index] = 'Contractor Name Required';
      }
    });

    if (isAddingExtension) {
      const newExtension = extensions[extensions.length - 1];

      const isEmpty =
        !newExtension.requestDate && !newExtension.expirationDate && !newExtension.delayExplanation?.trim();

      if (isEmpty) {
        extensionValidationErrors[newExtension.$index] = 'Extension Details Required';
      }
    }

    if (isAddingInspection) {
      const newInspection = inspections[inspections.length - 1];

      if (!newInspection.inspectionType?.trim()) {
        inspectionValidationErrors[newInspection.$index] = 'Inspection Type Required';
      }
    }

    setContractorErrors(contractorValidationErrors);
    setExtensionErrors(extensionValidationErrors);
    setInspectionErrors(inspectionValidationErrors);

    if (
      Object.keys(contractorValidationErrors).length > 0 ||
      Object.keys(extensionValidationErrors).length > 0 ||
      Object.keys(inspectionValidationErrors).length > 0
    ) {
      return;
    }

    setSaving(true);

    const updates: Partial<LossDraftRepair> = {
      ...pendingChanges,
      lastReviewedDate: dateToUnix(new Date()),
      lastReviewedBy: userId,
    };

    const success = await updateLossDraftRepair(updates);

    if (success) {
      setPendingChanges({});
      setContractorErrors({});
      setExtensionErrors({});
      setInspectionErrors({});
    }

    setSaving(false);
  };

  const handleCancel = () => {
    setPendingChanges({});
    setContractorErrors({});
    setExtensionErrors({});
    setInspectionErrors({});
  };

  const handleQidChange = (fieldName: QidFieldName, value: string) => {
    setPendingChanges((current) => ({
      ...current,
      [fieldName]: value,
    }));
  };

  const handleAddContractor = () => {
    if (isAddingContractor || contractors.length >= 10) {
      return;
    }

    const newContractor = {
      $index: contractors.length,
      contractorName: '',
      contractorContactName: '',
      bidAmount: null,
      lienWaiverAmount: null,
      payeeCode: '',
      detailsEnteredDate: dateToUnix(new Date()),
      bidReceivedDate: null,
      lienWaiverReceivedDate: null,
    };

    setPendingChanges((current) => ({
      ...current,
      contractor_array: [...contractors, newContractor],
    }));
  };

  const handleSaveContractor = async () => {
    if (!isAddingContractor) {
      return;
    }

    const newContractor = contractors[contractors.length - 1];

    if (!newContractor.contractorName?.trim()) {
      setContractorErrors((current) => ({
        ...current,
        [newContractor.$index]: 'Contractor Name Required',
      }));

      return;
    }

    setSaving(true);

    const success = await updateLossDraftRepair({
      contractor_array: contractors,
      lastReviewedDate: dateToUnix(new Date()),
      lastReviewedBy: userId,
    });

    if (success) {
      setPendingChanges((current) => {
        const { contractor_array, ...remainingChanges } = current;

        return remainingChanges;
      });

      setContractorErrors((current) => {
        const updatedErrors = { ...current };
        delete updatedErrors[newContractor.$index];
        return updatedErrors;
      });
    }

    setSaving(false);
  };

  const handleContractorChange = (
    index: number,
    fieldName: keyof LossDraftContractor,
    value: LossDraftContractor[keyof LossDraftContractor],
  ) => {
    const updatedContractors = contractors.map((contractor) =>
      contractor.$index === index
        ? {
            ...contractor,
            [fieldName]: value,
          }
        : contractor,
    );

    setPendingChanges((current) => ({
      ...current,
      contractor_array: updatedContractors,
    }));

    if (fieldName === 'contractorName' && String(value).trim() !== '') {
      setContractorErrors((current) => {
        const updated = { ...current };
        delete updated[index];
        return updated;
      });
    }
  };

  const handleCancelContractor = () => {
    if (!isAddingContractor) {
      return;
    }

    const newContractor = contractors[contractors.length - 1];

    setPendingChanges((current) => {
      if (!current.contractor_array) {
        return current;
      }

      const remainingContractors = current.contractor_array.slice(0, -1);
      const hasExistingContractorChanges = JSON.stringify(remainingContractors) !== JSON.stringify(savedContractors);

      if (hasExistingContractorChanges) {
        return {
          ...current,
          contractor_array: remainingContractors,
        };
      }

      const { contractor_array, ...remainingChanges } = current;

      return remainingChanges;
    });

    setContractorErrors((current) => {
      const updatedErrors = { ...current };
      delete updatedErrors[newContractor.$index];
      return updatedErrors;
    });

    setSaving(false);
  };

  const handleAddExtension = () => {
    if (isAddingExtension || extensions.length >= 10) {
      return;
    }

    const newExtension = {
      $index: extensions.length,
      requestDate: null,
      expirationDate: null,
      delayExplanation: '',
    };

    setPendingChanges((current) => ({
      ...current,
      extension_array: [...extensions, newExtension],
    }));
  };

  const handleExtensionChange = (
    index: number,
    changes: {
      requestDate?: number | null;
      expirationDate?: number | null;
      delayExplanation?: string;
    },
  ) => {
    const updatedExtensions = extensions.map((extension) =>
      extension.$index === index
        ? {
            ...extension,
            ...changes,
          }
        : extension,
    );

    setPendingChanges((current) => ({
      ...current,
      extension_array: updatedExtensions,
    }));

    setExtensionErrors((current) => {
      const updatedErrors = { ...current };
      delete updatedErrors[index];
      return updatedErrors;
    });
  };

  const handleUpdateExtension = async (index: number) => {
    const isNewExtension = isAddingExtension && index === extensions.length - 1;

    if (isAddingExtension) {
      const newExtension = extensions[index];

      const isEmpty =
        !newExtension.requestDate && !newExtension.expirationDate && !newExtension.delayExplanation?.trim();

      if (isEmpty) {
        setExtensionErrors((current) => ({
          ...current,
          [newExtension.$index]: 'Extension Details Required',
        }));

        return;
      }
    }

    setExtensionErrors({});
    setSaving(true);
    setSavingExtensionIndex(index);

    const extensionsToSave = isNewExtension ? extensions : extensions.slice(0, savedExtensions.length);

    const success = await updateLossDraftRepair({
      extension_array: extensionsToSave,
      lastReviewedDate: dateToUnix(new Date()),
      lastReviewedBy: userId,
    });

    if (success) {
      setPendingChanges((current) => {
        const { extension_array, ...remainingChanges } = current;

        if (isAddingExtension && !isNewExtension) {
          return {
            ...remainingChanges,
            extension_array: [...extensionsToSave, extensions[extensions.length - 1]],
          };
        }

        return remainingChanges;
      });
    }

    setSavingExtensionIndex(null);
    setSaving(false);
  };

  const hasExtensionChanges = (extension: LossDraftExtension) => {
    const savedExtension = savedExtensions.find((saved) => saved.$index === extension.$index);

    if (!savedExtension) {
      return true;
    }

    return (
      extension.requestDate !== savedExtension.requestDate ||
      extension.expirationDate !== savedExtension.expirationDate ||
      (extension.delayExplanation ?? '') !== (savedExtension.delayExplanation ?? '')
    );
  };

  const handleAddInspection = () => {
    if (isAddingInspection || inspections.length >= 10) {
      return;
    }

    const newInspection = {
      $index: inspections.length,
      inspectionType: '',
      requestDate: null,
      completionDate: null,
      reportDocId: '',
    };

    setPendingChanges((current) => ({
      ...current,
      inspection_array: [...inspections, newInspection],
    }));
  };

  const handleInspectionChange = (
    index: number,
    changes: {
      inspectionType?: string;
      requestDate?: number | null;
      completionDate?: number | null;
      reportDocId?: string;
    },
  ) => {
    const updatedInspections = inspections.map((inspection) =>
      inspection.$index === index
        ? {
            ...inspection,
            ...changes,
          }
        : inspection,
    );

    setPendingChanges((current) => ({
      ...current,
      inspection_array: updatedInspections,
    }));

    if (changes.inspectionType?.trim()) {
      setInspectionErrors((current) => {
        const updatedErrors = { ...current };
        delete updatedErrors[index];
        return updatedErrors;
      });
    }
  };

  const handleUpdateInspection = async (index: number) => {
    const isNewInspection = isAddingInspection && index === inspections.length - 1;

    if (isNewInspection) {
      const newInspection = inspections[index];

      if (!newInspection.inspectionType?.trim()) {
        setInspectionErrors((current) => ({
          ...current,
          [newInspection.$index]: 'Inspection Type Required',
        }));

        return;
      }
    }

    setInspectionErrors({});
    setSaving(true);
    setSavingInspectionIndex(index);

    const inspectionsToSave = isNewInspection ? inspections : inspections.slice(0, savedInspections.length);

    const success = await updateLossDraftRepair({
      inspection_array: inspectionsToSave,
      lastReviewedDate: dateToUnix(new Date()),
      lastReviewedBy: userId,
    });

    if (success) {
      setPendingChanges((current) => {
        const { inspection_array, ...remainingChanges } = current;

        if (isAddingInspection && !isNewInspection) {
          return {
            ...remainingChanges,
            inspection_array: [...inspectionsToSave, inspections[inspections.length - 1]],
          };
        }

        return remainingChanges;
      });

      setInspectionErrors((current) => {
        const updatedErrors = { ...current };
        delete updatedErrors[index];
        return updatedErrors;
      });
    }

    setSavingInspectionIndex(null);
    setSaving(false);
  };

  const hasInspectionChanges = (inspection: LossDraftInspection) => {
    const savedInspection = savedInspections.find((saved) => saved.$index === inspection.$index);

    if (!savedInspection) {
      return true;
    }

    return (
      (inspection.inspectionType ?? '') !== (savedInspection.inspectionType ?? '') ||
      inspection.requestDate !== savedInspection.requestDate ||
      inspection.completionDate !== savedInspection.completionDate ||
      (inspection.reportDocId ?? '') !== (savedInspection.reportDocId ?? '')
    );
  };

  const handleOpenDelayExplanation = (index: number, value: string) => {
    setDelayExplanationModal({
      index,
      value,
    });
  };

  const handleCancelDelayExplanation = () => {
    setDelayExplanationModal(null);
  };

  const handleSaveDelayExplanation = () => {
    if (!delayExplanationModal) {
      return;
    }

    handleExtensionChange(delayExplanationModal.index, {
      delayExplanation: delayExplanationModal.value,
    });

    setDelayExplanationModal(null);
  };

  return (
    <div className={styles.page}>
      <div className={styles.topActions}>
        <button type='button' className={styles.backButton} onClick={onBack}>
          Back to Claims
        </button>
      </div>

      <div className={styles.detailsContainer}>
        <div className={styles.lossDraftTitle}>Loss Draft Repair</div>

        <div className={styles.detailSummaryWrapper}>
          <table className={styles.detailSummaryTable}>
            <thead>
              <tr>
                <th>Date Initial Details Added</th>

                <th>Claim #</th>
                <th>Insurance Company Name</th>
                <th>Date of Loss</th>
                <th>Claim RCV</th>
              </tr>
            </thead>

            <tbody>
              <tr>
                <td>{formatDateValue(lossDraftRepair.dateInitialDetailsAdded)}</td>
                <td>
                  <input
                    type='text'
                    maxLength={12}
                    value={pendingChanges.claimNo ?? lossDraftRepair.claimNo ?? ''}
                    onChange={(e) =>
                      setPendingChanges((current) => ({
                        ...current,
                        claimNo: e.target.value,
                      }))
                    }
                  />
                </td>
                <td>
                  <input
                    type='text'
                    maxLength={20}
                    value={pendingChanges.insuranceCompanyName ?? lossDraftRepair.insuranceCompanyName ?? ''}
                    onChange={(e) =>
                      setPendingChanges((current) => ({
                        ...current,
                        insuranceCompanyName: e.target.value,
                      }))
                    }
                  />
                </td>
                <td>
                  <input
                    type='date'
                    value={formatDateValueForInput(pendingChanges.dateOfLoss ?? lossDraftRepair.dateOfLoss ?? '')}
                    max={formatDateYYYYMMDD(new Date())}
                    onKeyDown={(e) => e.preventDefault()}
                    onPaste={(e) => e.preventDefault()}
                    onChange={(e) =>
                      setPendingChanges((current) => ({
                        ...current,
                        dateOfLoss: dateInputToUnix(e.target.value),
                      }))
                    }
                  />
                </td>
                <td>
                  <NumericFormat
                    value={pendingChanges.claimRCV ?? lossDraftRepair.claimRCV ?? ''}
                    decimalScale={2}
                    fixedDecimalScale
                    thousandSeparator
                    prefix='$'
                    displayType='text'
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className={styles.twoColumnGrid}>
          <div>
            <FieldRow
              label='Claim status'
              value={
                <select
                  value={pendingChanges.claimStatus ?? lossDraftRepair.claimStatus ?? ''}
                  onChange={(e) => handleQidChange('claimStatus', e.target.value)}
                >
                  <option value='' disabled>
                    -- Select an Option --
                  </option>

                  {claimStatusQid.map((option, index) => (
                    <option key={index} value={option.code}>
                      {option.description}
                    </option>
                  ))}
                </select>
              }
            />
            <FieldRow
              label='Type of Loss'
              value={
                <select
                  value={pendingChanges.typeOfLoss ?? lossDraftRepair.typeOfLoss ?? ''}
                  onChange={(e) => handleQidChange('typeOfLoss', e.target.value)}
                >
                  <option value='' disabled>
                    -- Select an Option --
                  </option>

                  {typeOfLossQid.map((option, index) => (
                    <option key={index} value={option.code}>
                      {option.description}
                    </option>
                  ))}
                </select>
              }
            />
            <FieldRow
              label='Is the property a total loss'
              value={
                <select
                  value={
                    pendingChanges.totalLoss !== undefined
                      ? pendingChanges.totalLoss === null
                        ? ''
                        : String(pendingChanges.totalLoss)
                      : lossDraftRepair.totalLoss === null || lossDraftRepair.totalLoss === undefined
                        ? ''
                        : String(lossDraftRepair.totalLoss)
                  }
                  onChange={(e) =>
                    setPendingChanges((current) => ({
                      ...current,
                      totalLoss: e.target.value === '' ? null : e.target.value === 'true',
                    }))
                  }
                >
                  <option value='' disabled>
                    -- Select an Option --
                  </option>
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </select>
              }
            />
            <FieldRow
              label='Have we received a Loss draft check'
              value={
                <select
                  value={pendingChanges.lossDraftCheckStatus ?? lossDraftRepair.lossDraftCheckStatus ?? ''}
                  onChange={(e) => handleQidChange('lossDraftCheckStatus', e.target.value)}
                >
                  <option value='' disabled>
                    -- Select an Option --
                  </option>

                  {lossDraftCheckStatusQid.map((option, index) => (
                    <option key={index} value={option.code}>
                      {option.description}
                    </option>
                  ))}
                </select>
              }
            />
            <FieldRow
              label='Milestone status'
              value={
                <select
                  value={pendingChanges.milestoneStatus ?? lossDraftRepair.milestoneStatus ?? ''}
                  onChange={(e) => handleQidChange('milestoneStatus', e.target.value)}
                >
                  <option value='' disabled>
                    -- Select an Option --
                  </option>

                  {milestoneStatusQid.map((option, index) => (
                    <option key={index} value={option.code}>
                      {option.description}
                    </option>
                  ))}
                </select>
              }
            />
            <FieldRow
              label='Repair Due Date'
              value={formatDateValue(repairDueDate)}
              valueClassName={styles.calculatedValue}
            />
            <FieldRow
              label='Extension approved due date'
              value={
                <input
                  type='date'
                  value={formatDateValueForInput(
                    pendingChanges.extensionApprovedDueDate ?? lossDraftRepair.extensionApprovedDueDate,
                  )}
                  onChange={(e) =>
                    setPendingChanges((current) => ({
                      ...current,
                      extensionApprovedDueDate: dateInputToUnix(e.target.value),
                    }))
                  }
                />
              }
            />
            <FieldRow
              label='Last Default letter sent date'
              value={
                <input
                  type='date'
                  value={formatDateValueForInput(
                    pendingChanges.lastDefaultLetterSentDate ?? lossDraftRepair.lastDefaultLetterSentDate,
                  )}
                  max={formatDateYYYYMMDD(new Date())}
                  onChange={(e) =>
                    setPendingChanges((current) => ({
                      ...current,
                      lastDefaultLetterSentDate: dateInputToUnix(e.target.value),
                    }))
                  }
                />
              }
            />
            <FieldRow
              label='Repair file closed'
              value={
                <select
                  value={String(repairFileClosed)}
                  onChange={(e) => {
                    const closed = e.target.value === 'true';

                    setPendingChanges((current) => ({
                      ...current,
                      repairFileClosed: closed,
                      ...(!closed ? { repairFileClosedDate: null } : {}),
                    }));
                  }}
                >
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </select>
              }
            />
            <FieldRow
              label='Exceptions'
              value={
                <select
                  value={pendingChanges.exceptionCode ?? lossDraftRepair.exceptionCode ?? ''}
                  onChange={(e) => handleQidChange('exceptionCode', e.target.value)}
                >
                  <option value='' disabled>
                    -- Select an Option --
                  </option>

                  {exceptionCodeQid.map((option, index) => (
                    <option key={index} value={option.code}>
                      {option.description}
                    </option>
                  ))}
                </select>
              }
            />
            <FieldRow
              label='Repair file closed on'
              value={
                repairFileClosed ? (
                  <input
                    type='date'
                    value={formatDateValueForInput(
                      pendingChanges.repairFileClosedDate ?? lossDraftRepair.repairFileClosedDate,
                    )}
                    max={formatDateYYYYMMDD(new Date())}
                    onChange={(e) =>
                      setPendingChanges((current) => ({
                        ...current,
                        repairFileClosedDate: dateInputToUnix(e.target.value),
                      }))
                    }
                  />
                ) : (
                  formatDateValue(pendingChanges.repairFileClosedDate ?? lossDraftRepair.repairFileClosedDate)
                )
              }
            />
            <FieldRow
              label='Last reviewed on'
              value={formatDateValue(lossDraftRepair.lastReviewedDate)}
              valueClassName={styles.calculatedValue}
            />
          </div>

          <div>
            <FieldRow
              label='Replacement Cost Value (RCV)'
              value={
                <NumericFormat
                  value={pendingChanges.claimRCV ?? lossDraftRepair.claimRCV ?? ''}
                  decimalScale={2}
                  fixedDecimalScale
                  thousandSeparator
                  prefix='$'
                  allowNegative={false}
                  isAllowed={(values) => values.floatValue <= 999999999}
                  onValueChange={(values) =>
                    setPendingChanges((current) => ({
                      ...current,
                      claimRCV: values.floatValue ?? null,
                    }))
                  }
                />
              }
              valueClassName={styles.calculatedValue}
            />

            <FieldRow
              label='Delinquency Status When SWMC Notified of loss for FNMA / FHLMC'
              value={
                <select
                  value={pendingChanges.delinquencyStatus ?? lossDraftRepair.delinquencyStatus ?? ''}
                  onChange={(e) => handleQidChange('delinquencyStatus', e.target.value)}
                >
                  <option value='' disabled>
                    -- Select an Option --
                  </option>

                  {delinquencyStatusQid.map((option, index) => (
                    <option key={index} value={option.code}>
                      {option.description}
                    </option>
                  ))}
                </select>
              }
            />

            <FieldRow
              label='Last check received date'
              value={
                <input
                  type='date'
                  value={formatDateValueForInput(
                    pendingChanges.lastCheckReceivedDate ?? lossDraftRepair.lastCheckReceivedDate,
                  )}
                  max={formatDateYYYYMMDD(new Date())}
                  onChange={(e) => {
                    const lastCheckReceivedDate = dateInputToUnix(e.target.value);
                    const repairDueDate = addDaysToDateValue(lastCheckReceivedDate, 180);
                    const extensionFollowUpDate = addDaysToDateValue(repairDueDate, -15);
                    const defaultLetterDueDate = addBusinessDay(repairDueDate);

                    setPendingChanges((current) => ({
                      ...current,
                      lastCheckReceivedDate,
                      repairDueDate,
                      extensionFollowUpDate,
                      defaultLetterDueDate,
                    }));
                  }}
                />
              }
            />
            <FieldRow
              label='Extension follow up date'
              value={formatDateValue(extensionFollowUpDate)}
              valueClassName={styles.calculatedValue}
            />
            <FieldRow
              label='Default letter due date'
              value={formatDateValue(defaultLetterDueDate)}
              valueClassName={styles.calculatedValue}
            />
            <FieldRow
              label='Is the loan in default'
              value={isLoanInDefault(lossDraftRepair.repairDueDate)}
              valueClassName={styles.calculatedValue}
            />
            <FieldRow
              label='Repairs due in'
              value={getRepairsDueIn(lossDraftRepair.repairDueDate)}
              valueClassName={styles.calculatedValue}
            />

            <FieldRow
              label='Aging of the loan'
              value={agingOfLoan === null ? '' : `${agingOfLoan} day${agingOfLoan === 1 ? '' : 's'}`}
              valueClassName={styles.calculatedValue}
            />

            <FieldRow
              label='Repair file closed status'
              value={
                <select
                  value={pendingChanges.repairFileClosedStatus ?? lossDraftRepair.repairFileClosedStatus ?? ''}
                  onChange={(e) => handleQidChange('repairFileClosedStatus', e.target.value)}
                >
                  <option value='' disabled>
                    -- Select an Option --
                  </option>

                  {repairFileClosedStatusQid.map((option, index) => (
                    <option key={index} value={option.code}>
                      {option.description}
                    </option>
                  ))}
                </select>
              }
            />
            <FieldRow
              label='Last reviewed by'
              value={lossDraftRepair.lastReviewedBy || ''}
              valueClassName={styles.calculatedValue}
            />
          </div>
        </div>

        <div className={styles.contractorHeaderRow}>
          <div className={styles.subsectionTitle}>Contractor Details</div>

          <button
            type='button'
            className={styles.secondaryButton}
            onClick={handleAddContractor}
            disabled={contractors.length >= 10 || saving}
          >
            + Add another contractor
          </button>
        </div>

        {contractors.length === 0 ? (
          <div className={styles.emptySection}>No contractor details have been added.</div>
        ) : (
          contractors.map((contractor) => (
            <div className={styles.contractorBlock} key={contractor.$index}>
              {contractors.length > 1 && <div className={styles.itemTitle}>Contractor {contractor.$index + 1}</div>}

              <div className={styles.twoColumnGrid}>
                <div>
                  <FieldRow
                    label='Name [As shown in W-9]'
                    value={
                      <div className={styles.contractorFieldControl}>
                        <input
                          type='text'
                          maxLength={100}
                          className={contractorErrors[contractor.$index] ? styles.contractorInputError : ''}
                          value={contractor.contractorName ?? ''}
                          onChange={(e) => handleContractorChange(contractor.$index, 'contractorName', e.target.value)}
                        />
                        {contractorErrors[contractor.$index] && (
                          <div className={styles.contractorFieldError}>{contractorErrors[contractor.$index]}</div>
                        )}
                      </div>
                    }
                    valueClassName={styles.calculatedValue}
                  />
                  <FieldRow
                    label='Contact name'
                    value={
                      <input
                        type='text'
                        maxLength={100}
                        value={contractor.contractorContactName ?? ''}
                        onChange={(e) =>
                          handleContractorChange(contractor.$index, 'contractorContactName', e.target.value)
                        }
                      />
                    }
                    valueClassName={styles.calculatedValue}
                  />
                  <FieldRow
                    label='Bid amount'
                    value={
                      <NumericFormat
                        value={contractor.bidAmount ?? 0}
                        decimalScale={2}
                        fixedDecimalScale
                        thousandSeparator
                        prefix='$'
                        allowNegative={false}
                        isAllowed={(values) => values.floatValue <= 999999999}
                        onValueChange={(values) =>
                          handleContractorChange(contractor.$index, 'bidAmount', values.floatValue ?? null)
                        }
                      />
                    }
                    valueClassName={styles.calculatedValue}
                  />

                  <FieldRow
                    label='Lien waiver amount'
                    value={
                      <NumericFormat
                        value={contractor.lienWaiverAmount ?? 0}
                        decimalScale={2}
                        fixedDecimalScale
                        thousandSeparator
                        prefix='$'
                        allowNegative={false}
                        isAllowed={(values) => values.floatValue <= 999999999}
                        onValueChange={(values) =>
                          handleContractorChange(contractor.$index, 'lienWaiverAmount', values.floatValue ?? null)
                        }
                      />
                    }
                    valueClassName={styles.calculatedValue}
                  />

                  <FieldRow
                    label='Payee Code'
                    value={
                      <input
                        type='text'
                        maxLength={20}
                        value={contractor.payeeCode ?? ''}
                        onChange={(e) => handleContractorChange(contractor.$index, 'payeeCode', e.target.value)}
                      />
                    }
                    valueClassName={styles.calculatedValue}
                  />
                </div>

                <div>
                  <FieldRow
                    label='Details entered date'
                    value={formatDateValue(contractor.detailsEnteredDate)}
                    valueClassName={styles.calculatedValue}
                  />
                  <FieldRow
                    label='Bid received date'
                    value={
                      <input
                        type='date'
                        value={formatDateValueForInput(contractor.bidReceivedDate)}
                        max={formatDateYYYYMMDD(new Date())}
                        onKeyDown={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                        onChange={(e) =>
                          handleContractorChange(contractor.$index, 'bidReceivedDate', dateInputToUnix(e.target.value))
                        }
                      />
                    }
                  />
                  <FieldRow
                    label='Lien waiver received date'
                    value={
                      <input
                        type='date'
                        value={formatDateValueForInput(contractor.lienWaiverReceivedDate)}
                        max={formatDateYYYYMMDD(new Date())}
                        onKeyDown={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                        onChange={(e) =>
                          handleContractorChange(
                            contractor.$index,
                            'lienWaiverReceivedDate',
                            dateInputToUnix(e.target.value),
                          )
                        }
                      />
                    }
                  />
                </div>
              </div>
            </div>
          ))
        )}

        <div className={styles.twoColumnGrid}>
          <div>
            <FieldRow
              label='No contractor involved form received'
              value={
                <select
                  value={String(
                    pendingChanges.noContractorFormReceived ?? lossDraftRepair.noContractorFormReceived ?? false,
                  )}
                  onChange={(e) =>
                    setPendingChanges((current) => ({
                      ...current,
                      noContractorFormReceived: e.target.value === 'true',
                    }))
                  }
                >
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </select>
              }
            />
            <FieldRow
              label='No contractor form received date'
              value={
                <input
                  type='date'
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  value={formatDateValueForInput(
                    pendingChanges.noContractorFormDate ?? lossDraftRepair.noContractorFormDate,
                  )}
                  max={formatDateYYYYMMDD(new Date())}
                  onChange={(e) =>
                    setPendingChanges((current) => ({
                      ...current,
                      noContractorFormDate: dateInputToUnix(e.target.value),
                    }))
                  }
                />
              }
            />
            <FieldRow
              label='Have we received authorization to issue direct payment to the contractor'
              value={
                <select
                  value={String(pendingChanges.directPayAuthReceived ?? lossDraftRepair.directPayAuthReceived ?? false)}
                  onChange={(e) =>
                    setPendingChanges((current) => ({
                      ...current,
                      directPayAuthReceived: e.target.value === 'true',
                    }))
                  }
                >
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </select>
              }
            />
            <FieldRow
              label='Direct payment authorization received date'
              value={
                <input
                  type='date'
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  value={formatDateValueForInput(pendingChanges.directPayAuthDate ?? lossDraftRepair.directPayAuthDate)}
                  max={formatDateYYYYMMDD(new Date())}
                  onChange={(e) =>
                    setPendingChanges((current) => ({
                      ...current,
                      directPayAuthDate: dateInputToUnix(e.target.value),
                    }))
                  }
                />
              }
            />
            <FieldRow
              label='If yes, specify the name of the contractor'
              value={
                <input
                  type='text'
                  maxLength={50}
                  value={pendingChanges.directPayContractorName ?? lossDraftRepair.directPayContractorName ?? ''}
                  onChange={(e) =>
                    setPendingChanges((current) => ({
                      ...current,
                      directPayContractorName: e.target.value,
                    }))
                  }
                />
              }
              valueClassName={styles.calculatedValue}
            />
          </div>

          <div>
            <FieldRow
              label='Full Responsibility letter received'
              value={
                <select
                  value={String(
                    pendingChanges.fullRespLetterReceived ?? lossDraftRepair.fullRespLetterReceived ?? false,
                  )}
                  onChange={(e) =>
                    setPendingChanges((current) => ({
                      ...current,
                      fullRespLetterReceived: e.target.value === 'true',
                    }))
                  }
                >
                  <option value='true'>Yes</option>
                  <option value='false'>No</option>
                </select>
              }
            />
            <FieldRow
              label='Full Responsibility letter received date'
              value={
                <input
                  type='date'
                  onKeyDown={(e) => e.preventDefault()}
                  onPaste={(e) => e.preventDefault()}
                  value={formatDateValueForInput(
                    pendingChanges.fullRespLetterDate ?? lossDraftRepair.fullRespLetterDate,
                  )}
                  max={formatDateYYYYMMDD(new Date())}
                  onChange={(e) =>
                    setPendingChanges((current) => ({
                      ...current,
                      fullRespLetterDate: dateInputToUnix(e.target.value),
                    }))
                  }
                />
              }
            />
            <div className={styles.notesBlock}>
              <div className={styles.fieldLabel}>Important notes</div>

              <textarea
                className={styles.notesInput}
                maxLength={4000}
                value={pendingChanges.importantNotes ?? lossDraftRepair.importantNotes ?? ''}
                onChange={(e) =>
                  setPendingChanges((current) => ({
                    ...current,
                    importantNotes: e.target.value,
                  }))
                }
              />
            </div>
          </div>
        </div>

        {isAddingContractor && (
          <div className={styles.contractorActions}>
            <button type='button' className={styles.secondaryButton} onClick={handleCancelContractor} disabled={saving}>
              Cancel
            </button>

            <button type='button' className={styles.secondaryButton} onClick={handleSaveContractor} disabled={saving}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}

        <div className={styles.subsectionHeader}>
          <span>Repairs Extension</span>
          <button
            type='button'
            className={styles.headerButton}
            onClick={handleAddExtension}
            disabled={isAddingExtension || extensions.length >= 10 || saving}
          >
            + Add new extension
          </button>
        </div>

        <div className={styles.tableWrapper}>
          <table className={`${styles.detailTable} ${styles.extensionTable}`}>
            <thead>
              <tr>
                <th>Extension</th>
                <th>Request Date</th>
                <th>Expiration Date</th>
                <th>Delay Explanation</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {extensions.length === 0 ? (
                <tr>
                  <td className={styles.emptyTable} colSpan={5}>
                    No repair extensions have been added.
                  </td>
                </tr>
              ) : (
                extensions.map((extension) => (
                  <tr key={extension.$index} className={styles.extensionRow}>
                    <td>Extension {extension.$index + 1}</td>
                    <td>
                      <input
                        type='date'
                        value={formatDateValueForInput(extension.requestDate)}
                        max={formatDateYYYYMMDD(new Date())}
                        onKeyDown={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                        onChange={(e) =>
                          handleExtensionChange(extension.$index, {
                            requestDate: dateInputToUnix(e.target.value),
                          })
                        }
                      />
                      {extensionErrors[extension.$index] && (
                        <div className={styles.claimValidationError}>{extensionErrors[extension.$index]}</div>
                      )}
                    </td>
                    <td>
                      <input
                        type='date'
                        value={formatDateValueForInput(extension.expirationDate)}
                        onKeyDown={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                        onChange={(e) =>
                          handleExtensionChange(extension.$index, {
                            expirationDate: dateInputToUnix(e.target.value),
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type='text'
                        value={extension.delayExplanation ?? ''}
                        maxLength={500}
                        readOnly
                        onClick={() => handleOpenDelayExplanation(extension.$index, extension.delayExplanation ?? '')}
                      />
                    </td>

                    <td className={styles.actionCell}>
                      <button
                        type='button'
                        className={styles.updateButton}
                        onClick={() => handleUpdateExtension(extension.$index)}
                        disabled={saving || !hasExtensionChanges(extension)}
                      >
                        {savingExtensionIndex == extension.$index
                          ? 'Saving...'
                          : isAddingExtension && extension.$index === extensions.length - 1
                            ? 'Create'
                            : 'Update'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className={styles.subsectionHeader}>
          <span>Inspection Status</span>
          <button
            type='button'
            className={styles.headerButton}
            onClick={handleAddInspection}
            disabled={isAddingInspection || inspections.length >= 10 || saving}
          >
            + Add new inspection
          </button>
        </div>

        <div className={styles.tableWrapper}>
          <table className={`${styles.detailTable} ${styles.inspectionTable}`}>
            <thead>
              <tr>
                <th>Inspection</th>
                <th>Request Date</th>
                <th>Completion Date</th>
                <th>Report doc ID</th>
                <th />
              </tr>
            </thead>

            <tbody>
              {inspections.length === 0 ? (
                <tr>
                  <td className={styles.emptyTable} colSpan={5}>
                    No inspections have been added.
                  </td>
                </tr>
              ) : (
                inspections.map((inspection) => (
                  <tr key={inspection.$index} className={styles.inspectionRow}>
                    <td>
                      <select
                        value={inspection.inspectionType ?? ''}
                        onChange={(e) =>
                          handleInspectionChange(inspection.$index, {
                            inspectionType: e.target.value,
                          })
                        }
                      >
                        <option value='' disabled>
                          Select
                        </option>

                        {inspectionTypeQid.map((option, index) => (
                          <option key={index} value={option.code}>
                            {option.description}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input
                        type='date'
                        value={formatDateValueForInput(inspection.requestDate)}
                        max={formatDateYYYYMMDD(new Date())}
                        onKeyDown={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                        onChange={(e) =>
                          handleInspectionChange(inspection.$index, {
                            requestDate: dateInputToUnix(e.target.value),
                          })
                        }
                      />
                      {inspectionErrors[inspection.$index] && (
                        <div className={styles.claimValidationError}>{inspectionErrors[inspection.$index]}</div>
                      )}
                    </td>
                    <td>
                      <input
                        type='date'
                        value={formatDateValueForInput(inspection.completionDate)}
                        onKeyDown={(e) => e.preventDefault()}
                        onPaste={(e) => e.preventDefault()}
                        onChange={(e) =>
                          handleInspectionChange(inspection.$index, {
                            completionDate: dateInputToUnix(e.target.value),
                          })
                        }
                      />
                    </td>
                    <td>
                      <input
                        type='text'
                        value={inspection.reportDocId ?? ''}
                        maxLength={9}
                        onChange={(e) =>
                          handleInspectionChange(inspection.$index, {
                            reportDocId: e.target.value,
                          })
                        }
                      />
                    </td>

                    <td className={styles.actionCell}>
                      <button
                        type='button'
                        className={styles.updateButton}
                        onClick={() => handleUpdateInspection(inspection.$index)}
                        disabled={saving || !hasInspectionChanges(inspection)}
                      >
                        {savingInspectionIndex == inspection.$index
                          ? 'Saving...'
                          : isAddingInspection && inspection.$index === inspections.length - 1
                            ? 'Create'
                            : 'Update'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className={styles.saveActions}>
          <button
            type='button'
            className={styles.secondaryButton}
            onClick={handleCancel}
            disabled={!hasPendingChanges || saving}
          >
            Cancel
          </button>

          <button
            type='button'
            className={styles.secondaryButton}
            onClick={handleSave}
            disabled={!hasPendingChanges || saving}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
      {delayExplanationModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.delayExplanationModal}>
            <div className={styles.delayExplanationModalHeader}>Delay Explanation</div>

            <textarea
              className={styles.delayExplanationTextarea}
              value={delayExplanationModal.value}
              maxLength={500}
              placeholder='Enter delay explanation...'
              autoFocus
              onChange={(e) =>
                setDelayExplanationModal((current) =>
                  current
                    ? {
                        ...current,
                        value: e.target.value,
                      }
                    : null,
                )
              }
            />

            <div className={styles.characterCount}>{delayExplanationModal.value.length} / 500</div>

            <div className={styles.delayExplanationModalActions}>
              <button type='button' className={styles.secondaryButton} onClick={handleCancelDelayExplanation}>
                Cancel
              </button>

              <button type='button' className={styles.secondaryButton} onClick={handleSaveDelayExplanation}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LossDraftRepairDetails;
